/**
 * Email Templates Admin Page
 *
 * Admin-only page for browsing and editing EQuIP email templates.
 * Features a floating file drawer and expansive editor layout.
 */

import { useEffect, useState, useCallback, useRef } from 'react'
import { useAuth } from '@/context/AuthContext'
import { useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'motion/react'
import { Mail, Shield, PanelLeftOpen, PanelLeftClose, X, Loader2, Plus, FolderPlus } from 'lucide-react'
import { useEmailTemplates } from '@/hooks/useEmailTemplates'
import { useEmailTemplateAPI } from '@/hooks/useEmailTemplateAPI'
import { TreeNavigator, TemplatePreview, EmptyState, ChatPanel, CreateTemplateDialog, CreateFolderDialog, DeleteConfirmDialog, MoveToDialog, RenameDialog } from '@/components/email-templates'
import { FloatingNav } from '@/components/layout'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { toast } from '@/components/ui/sonner'
import { useEmailFolderAPI } from '@/hooks/useEmailFolderAPI'
import { stripNodePrefix, type EmailTemplateCreate } from '@/lib/email-templates'
import type { EmailFolderCreate } from '@/lib/email-templates/folder-types'
import { cn } from '@/lib/utils'

/** Tracks the item being moved via the MoveToDialog */
interface MoveTarget {
  type: 'template' | 'folder'
  id: string
  name: string
}

export function EmailTemplates() {
  const [searchParams, setSearchParams] = useSearchParams()
  const templateIdFromUrl = searchParams.get('template')
  const [copied, setCopied] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState<string | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(true)
  const [drawerWidth, setDrawerWidth] = useState(() => {
    // Load saved width from localStorage or default to 320px
    const saved = localStorage.getItem('emailTemplates.drawerWidth')
    return saved ? parseInt(saved, 10) : 320
  })
  const [isResizing, setIsResizing] = useState(false)
  const drawerRef = useRef<HTMLDivElement>(null)
  const [isAiProcessing, setIsAiProcessing] = useState(false)
  const [originalContent, setOriginalContent] = useState<string | null>(null)
  const [pendingAiHtml, setPendingAiHtml] = useState<string | null>(null)
  const [previewContent, setPreviewContent] = useState<string | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false)

  // Move dialog state
  const [moveDialogOpen, setMoveDialogOpen] = useState(false)
  const [moveTarget, setMoveTarget] = useState<MoveTarget | null>(null)

  // Delete folder confirmation state
  const [deleteFolderConfirmOpen, setDeleteFolderConfirmOpen] = useState(false)
  const [deleteFolderTarget, setDeleteFolderTarget] = useState<{ id: number; name: string } | null>(null)

  // Rename dialog state
  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  const [renameTarget, setRenameTarget] = useState<{ type: 'template' | 'folder'; id: string; name: string } | null>(null)

  const { user } = useAuth()

  const { templateTree, selectedTemplate, selectedId, selectTemplate, clearSelection, isLoading, folders, invalidate } = useEmailTemplates({
    initialTemplateId: templateIdFromUrl,
  })

  const { fetchTemplate, createTemplate, updateTemplate, patchTemplate, deleteTemplate, isLoading: isSaving } = useEmailTemplateAPI()
  const { createFolder, patchFolder, deleteFolder, isLoading: isFolderApiLoading } = useEmailFolderAPI()

  const [createTemplateOpen, setCreateTemplateOpen] = useState(false)
  const [createFolderOpen, setCreateFolderOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null)

  // Update URL when template selection changes
  useEffect(() => {
    if (selectedId) {
      setSearchParams({ template: selectedId }, { replace: true })
    } else {
      setSearchParams({}, { replace: true })
    }
  }, [selectedId, setSearchParams])

  // Exit edit mode and fetch preview content when changing templates
  useEffect(() => {
    if (isEditing) {
      setIsEditing(false)
      setEditContent(null)
    }
    // Fetch content for preview
    if (selectedTemplate) {
      setIsLoadingPreview(true)
      fetchTemplate(selectedTemplate.id).then((detail) => {
        if (detail) {
          setPreviewContent(detail.htmlContent)
        }
        setIsLoadingPreview(false)
      })
    } else {
      setPreviewContent(null)
    }
  }, [selectedId, selectedTemplate, fetchTemplate])


  // Copy shareable link to clipboard
  const copyShareableLink = () => {
    if (selectedId) {
      const url = `${window.location.origin}${window.location.pathname}#/admin/email-templates?template=${selectedId}`
      navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  // Handle Edit button click - fetch template from database
  const handleEdit = async () => {
    if (!selectedTemplate) return

    const detail = await fetchTemplate(selectedTemplate.id)
    if (detail) {
      setEditContent(detail.htmlContent)
      setOriginalContent(detail.htmlContent) // Store original for revert
      setIsEditing(true)
    } else {
      toast.error('Failed to load template for editing')
    }
  }

  // Handle Save - update template via API
  const handleSave = async (htmlContent: string) => {
    if (!selectedTemplate) return

    const result = await updateTemplate(selectedTemplate.id, { htmlContent })
    if (result) {
      toast.success('Template saved successfully')
      setPreviewContent(htmlContent)
      setIsEditing(false)
      setEditContent(null)
    } else {
      toast.error('Failed to save template')
    }
  }

  // Handle Cancel - exit edit mode without saving
  const handleCancel = () => {
    setIsEditing(false)
    setEditContent(null)
    setOriginalContent(null)
    setPendingAiHtml(null)
  }

  // Handle AI HTML update from ChatPanel
  const handleAiHtmlUpdate = (html: string) => {
    // Validate HTML - must contain basic HTML structure
    const isValid = html.includes('<html') || html.includes('<body') || html.includes('<table')

    if (!isValid) {
      toast.error('AI returned invalid HTML. Please try again.')
      return
    }

    // Set pending HTML for editor to apply
    setPendingAiHtml(html)
  }

  // Handle AI changes applied in editor
  const handleAiApplied = useCallback((appliedHtml: string) => {
    // Update edit content to match what was applied
    setEditContent(appliedHtml)
    // Clear pending HTML
    setPendingAiHtml(null)
    // Show success toast
    toast.success('Template updated')
  }, [])

  // Handle revert to original
  const handleRevert = () => {
    if (originalContent) {
      setEditContent(originalContent)
      setPendingAiHtml(null)
      toast.info('Template reverted to original')
    }
  }

  // Handle AI processing state change
  const handleAiProcessingChange = (processing: boolean) => {
    setIsAiProcessing(processing)
  }

  // Handle Test Email - send template to logged-in user
  const handleTestEmail = async () => {
    if (!selectedTemplate || !previewContent) return

    if (!user?.email) {
      toast.error('Unable to determine your email address')
      return
    }

    const logicAppUrl = import.meta.env.VITE_EMAIL_LOGIC_APP_URL
    if (!logicAppUrl) {
      toast.error('Email service not configured')
      return
    }

    setIsSendingTestEmail(true)
    try {
      const payload = {
        to: user.email,
        from: 'training@eventuswh.com',
        subject: `[TEST EMAIL] ${selectedTemplate.subject}`,
        body: previewContent
      }

      const response = await fetch(logicAppUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      if (response.ok) {
        toast.success(`Test email sent to ${user.email}`)
      } else {
        toast.error('Failed to send test email')
      }
    } catch (err) {
      console.error('Test email error:', err)
      toast.error('Failed to send test email')
    } finally {
      setIsSendingTestEmail(false)
    }
  }

  // Handle template selection from drawer (now receives namespaced IDs)
  const handleSelectTemplate = (nodeId: string) => {
    const { type, id } = stripNodePrefix(nodeId)
    if (type === 'template') {
      selectTemplate(id)
      setSelectedFolderId(null)
      if (window.innerWidth < 1024) {
        setDrawerOpen(false)
      }
    } else if (type === 'folder') {
      setSelectedFolderId(parseInt(id, 10))
    }
  }

  // Handle create template
  const handleCreateTemplate = async (data: EmailTemplateCreate) => {
    const result = await createTemplate(data)
    if (result) {
      // toast.success(`Template "${data.name}" created`)
      setCreateTemplateOpen(false)
      invalidate()
      selectTemplate(data.templateKey)
    } else {
      toast.error('Failed to create template')
    }
  }

  // Handle create folder
  const handleCreateFolder = async (data: EmailFolderCreate) => {
    const result = await createFolder(data)
    if (result) {
      // toast.success(`Folder "${data.name}" created`)
      setCreateFolderOpen(false)
      invalidate()
    } else {
      toast.error('Failed to create folder')
    }
  }

  // Handle delete template
  const handleDeleteTemplate = async () => {
    if (!selectedTemplate) return
    const success = await deleteTemplate(selectedTemplate.id)
    if (success) {
      // toast.success(`Template "${selectedTemplate.name}" deleted`)
      setDeleteConfirmOpen(false)
      clearSelection()
      invalidate()
    } else {
      toast.error('Failed to delete template')
    }
  }

  // Handle duplicate template (context menu)
  const handleDuplicateTemplate = async (templateKey: string) => {
    const detail = await fetchTemplate(templateKey)
    if (!detail) {
      toast.error('Failed to load template for duplication')
      return
    }

    // Generate a unique copy key
    let copyKey = `${detail.templateKey}-copy`
    let copyName = `${detail.name} (Copy)`

    // Check if -copy already exists by trying with a suffix
    const existingKeys = new Set<string>()
    function collectKeys(node: typeof templateTree) {
      if (node.template) existingKeys.add(node.template.id)
      node.children?.forEach(collectKeys)
    }
    collectKeys(templateTree)

    if (existingKeys.has(copyKey)) {
      let i = 2
      while (existingKeys.has(`${detail.templateKey}-copy-${i}`)) {
        i++
      }
      copyKey = `${detail.templateKey}-copy-${i}`
      copyName = `${detail.name} (Copy ${i})`
    }

    const newTemplate: EmailTemplateCreate = {
      templateKey: copyKey,
      name: copyName,
      category: detail.category,
      audience: detail.audience,
      subject: detail.subject,
      description: detail.description ?? undefined,
      htmlContent: detail.htmlContent,
      folderId: detail.folderId,
    }

    const result = await createTemplate(newTemplate)
    if (result) {
      invalidate()
      selectTemplate(copyKey)
    } else {
      toast.error('Failed to duplicate template')
    }
  }

  // Handle move template (drag-and-drop or MoveToDialog)
  const handleMoveTemplate = async (templateKey: string, targetFolderId: number | null) => {
    const result = await patchTemplate(templateKey, { folderId: targetFolderId })
    if (result) {
      invalidate()
    } else {
      toast.error('Failed to move template')
    }
  }

  // Handle move folder (drag-and-drop or MoveToDialog)
  const handleMoveFolder = async (folderId: number, targetParentId: number | null) => {
    const result = await patchFolder(folderId, { parentId: targetParentId })
    if (result) {
      invalidate()
    } else {
      toast.error('Failed to move folder')
    }
  }

  // Handle delete template from context menu (opens existing dialog)
  const handleDeleteTemplateFromTree = (templateKey: string) => {
    // Select the template first so the delete dialog has context
    selectTemplate(templateKey)
    setDeleteConfirmOpen(true)
  }

  // Handle delete folder from context menu
  const handleDeleteFolderFromTree = (folderId: number) => {
    const folder = folders.find((f) => f.id === folderId)
    setDeleteFolderTarget({ id: folderId, name: folder?.name ?? 'this folder' })
    setDeleteFolderConfirmOpen(true)
  }

  const handleConfirmDeleteFolder = async () => {
    if (!deleteFolderTarget) return
    const success = await deleteFolder(deleteFolderTarget.id)
    if (success) {
      // toast.success(`Folder "${deleteFolderTarget.name}" deleted`)
      setDeleteFolderConfirmOpen(false)
      setDeleteFolderTarget(null)
      invalidate()
    } else {
      toast.error('Folder is not empty. Move or delete its contents first.')
      setDeleteFolderConfirmOpen(false)
      setDeleteFolderTarget(null)
    }
  }

  // Handle rename template (from tree context menu)
  const handleRenameTemplateFromTree = (templateKey: string, currentName: string) => {
    setRenameTarget({ type: 'template', id: templateKey, name: currentName })
    setRenameDialogOpen(true)
  }

  // Handle rename folder (from tree context menu)
  const handleRenameFolderFromTree = (folderId: number, currentName: string) => {
    setRenameTarget({ type: 'folder', id: String(folderId), name: currentName })
    setRenameDialogOpen(true)
  }

  // Handle rename submit (from dialog or inline editor)
  const handleRenameSubmit = async (newName: string) => {
    if (!renameTarget) return

    if (renameTarget.type === 'template') {
      const result = await patchTemplate(renameTarget.id, { name: newName })
      if (result) {
        toast.success(`Renamed to "${newName}"`)
        invalidate()
      } else {
        toast.error('Failed to rename template')
      }
    } else if (renameTarget.type === 'folder') {
      const result = await patchFolder(parseInt(renameTarget.id, 10), { name: newName })
      if (result) {
        toast.success(`Renamed to "${newName}"`)
        invalidate()
      } else {
        toast.error('Failed to rename folder')
      }
    }

    setRenameDialogOpen(false)
    setRenameTarget(null)
  }

  // Handle rename from editor title (inline)
  const handleRenameFromEditor = async (newName: string) => {
    if (!selectedTemplate) return

    const result = await patchTemplate(selectedTemplate.id, { name: newName })
    if (result) {
      toast.success(`Renamed to "${newName}"`)
      invalidate()
    } else {
      toast.error('Failed to rename template')
    }
  }

  // Handle "Move to..." from context menu — opens MoveToDialog
  const handleRequestMoveTo = (nodeId: string) => {
    const { type, id } = stripNodePrefix(nodeId)

    if (type === 'template') {
      // Find the template name from the tree
      let name = id
      function findTemplateName(node: typeof templateTree): string | null {
        if (node.template?.id === id) return node.name
        for (const child of node.children ?? []) {
          const found = findTemplateName(child)
          if (found) return found
        }
        return null
      }
      name = findTemplateName(templateTree) ?? id

      setMoveTarget({ type: 'template', id, name })
      setMoveDialogOpen(true)
    } else if (type === 'folder') {
      const folder = folders.find((f) => f.id === parseInt(id, 10))
      setMoveTarget({ type: 'folder', id, name: folder?.name ?? id })
      setMoveDialogOpen(true)
    }
  }

  // Handle MoveToDialog submit — routes to moveTemplate or moveFolder
  const handleMoveDialogSubmit = async (targetFolderId: number | null) => {
    if (!moveTarget) return

    if (moveTarget.type === 'template') {
      await handleMoveTemplate(moveTarget.id, targetFolderId)
    } else if (moveTarget.type === 'folder') {
      await handleMoveFolder(parseInt(moveTarget.id, 10), targetFolderId)
    }

    setMoveDialogOpen(false)
    setMoveTarget(null)
  }

  // Handle drawer resize
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
  }, [])

  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = Math.min(Math.max(e.clientX, 280), 600) // Min 280px, max 600px
      setDrawerWidth(newWidth)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      // Save to localStorage
      localStorage.setItem('emailTemplates.drawerWidth', drawerWidth.toString())
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing, drawerWidth])

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-slate-50">
      {/* Site Navigation */}
      <FloatingNav items={[]} showHome />

      {/* Compact Header - Shrinks when editing */}
      <header
        className={cn(
          "flex-shrink-0 bg-gradient-to-r from-eventus-navy via-eventus-navy to-eventus-purple/80 transition-all duration-300",
          isEditing ? "py-3 pt-16" : "py-6 pt-20"
        )}
      >
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              {/* Drawer Toggle */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setDrawerOpen(!drawerOpen)}
                className={cn(
                  "p-2 rounded-lg transition-colors",
                  drawerOpen
                    ? "bg-white/20 text-white"
                    : "bg-white/10 text-white/80 hover:bg-white/20 hover:text-white"
                )}
                title={drawerOpen ? "Hide templates" : "Show templates"}
              >
                {drawerOpen ? (
                  <PanelLeftClose className="w-5 h-5" />
                ) : (
                  <PanelLeftOpen className="w-5 h-5" />
                )}
              </motion.button>

              {/* Icon + Title */}
              <div className="flex items-center gap-3">
                <div className={cn(
                  "bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/20 transition-all",
                  isEditing ? "w-9 h-9" : "w-11 h-11"
                )}>
                  <Mail className={cn("text-eventus-teal transition-all", isEditing ? "w-4 h-4" : "w-5 h-5")} />
                </div>
                <div>
                  <h1 className={cn(
                    "font-semibold text-white transition-all",
                    isEditing ? "text-lg" : "text-xl"
                  )}>
                    Email Templates
                  </h1>
                </div>
              </div>
            </div>

            {/* Status badges */}
            <div className="flex items-center gap-2">
              {isEditing && selectedTemplate && (
                <Badge className="bg-amber-500/20 text-amber-200 border-amber-400/30 animate-pulse">
                  Editing: {selectedTemplate.name}
                </Badge>
              )}
              <Badge className="bg-white/10 text-white/80 border-white/20">
                <Shield className="w-3 h-3 mr-1" />
                Admin
              </Badge>
            </div>
          </motion.div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 overflow-hidden relative">
        {/* Floating Drawer Overlay - slides in from left */}
        <AnimatePresence>
          {drawerOpen && (
            <>
              {/* Backdrop on mobile */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="lg:hidden fixed inset-0 bg-black/40 z-20"
                onClick={() => setDrawerOpen(false)}
              />

              {/* Drawer */}
              <motion.div
                ref={drawerRef}
                initial={{ x: -320, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -320, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                style={{ width: drawerWidth }}
                className={cn(
                  "absolute left-0 top-0 bottom-0 z-30",
                  "bg-white shadow-2xl shadow-black/10",
                  "border-r border-slate-200",
                  "flex flex-col"
                )}
              >
                {/* Drawer Header */}
                <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/80">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    {isEditing ? 'AI Assistant' : 'Templates'}
                  </span>
                  <div className="flex items-center gap-1">
                    {!isEditing && (
                      <>
                        <button
                          onClick={() => setCreateTemplateOpen(true)}
                          className="p-1.5 rounded-md hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                          title="New template"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setCreateFolderOpen(true)}
                          className="p-1.5 rounded-md hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                          title="New folder"
                        >
                          <FolderPlus className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => setDrawerOpen(false)}
                      className="p-1.5 rounded-md hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors lg:hidden"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Tree Navigator or Chat Panel - fills remaining space */}
                <div className="flex-1 overflow-hidden">
                  {isEditing && editContent && selectedTemplate ? (
                    <ChatPanel
                      currentHtml={editContent}
                      templateName={selectedTemplate.name}
                      onHtmlUpdate={handleAiHtmlUpdate}
                      onAiProcessingChange={handleAiProcessingChange}
                    />
                  ) : isLoading ? (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400">
                      <Loader2 className="w-8 h-8 mb-3 animate-spin" />
                      <p className="text-sm">Loading templates...</p>
                    </div>
                  ) : (
                    <TreeNavigator
                      tree={templateTree}
                      selectedId={selectedId}
                      onSelect={handleSelectTemplate}
                      onDuplicateTemplate={handleDuplicateTemplate}
                      onMoveTemplate={handleMoveTemplate}
                      onMoveFolder={handleMoveFolder}
                      onDeleteTemplate={handleDeleteTemplateFromTree}
                      onDeleteFolder={handleDeleteFolderFromTree}
                      onRequestMoveTo={handleRequestMoveTo}
                      onRenameTemplate={handleRenameTemplateFromTree}
                      onRenameFolder={handleRenameFolderFromTree}
                      onCreateTemplateInFolder={(folderId) => {
                        setSelectedFolderId(folderId === 0 ? null : folderId)
                        setCreateTemplateOpen(true)
                      }}
                      onCreateFolderInFolder={(parentId) => {
                        setSelectedFolderId(parentId)
                        setCreateFolderOpen(true)
                      }}
                    />
                  )}
                </div>

                {/* Resize Handle */}
                <div
                  onMouseDown={handleResizeStart}
                  className={cn(
                    "absolute right-0 top-0 bottom-0 w-1 cursor-col-resize",
                    "hover:bg-eventus-purple/30 active:bg-eventus-purple/50",
                    "transition-colors hidden lg:block",
                    isResizing && "bg-eventus-purple/50"
                  )}
                >
                  {/* Visual grip indicator */}
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-full bg-slate-300 opacity-0 hover:opacity-100 transition-opacity" />
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Content Area - shifts based on drawer state (only on lg+ screens) */}
        <style>{`
          @media (min-width: 1024px) {
            .drawer-content-area-open {
              margin-left: ${drawerWidth}px;
            }
          }
        `}</style>
        <div
          className={cn(
            "h-full transition-all duration-300",
            drawerOpen && "drawer-content-area-open"
          )}
        >
          <div className="h-full p-4 sm:p-6">
            <Card className="h-full overflow-hidden border-slate-200 shadow-sm">
              {selectedTemplate ? (
                <TemplatePreview
                  template={selectedTemplate}
                  onShareLink={copyShareableLink}
                  copied={copied}
                  isEditing={isEditing}
                  onEdit={handleEdit}
                  editContent={editContent}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  isSaving={isSaving}
                  isAiProcessing={isAiProcessing}
                  pendingAiHtml={pendingAiHtml}
                  onAiApplied={handleAiApplied}
                  originalContent={originalContent}
                  onRevert={handleRevert}
                  previewContent={previewContent}
                  isLoadingPreview={isLoadingPreview}
                  onTestEmail={handleTestEmail}
                  isSendingTestEmail={isSendingTestEmail}
                  onDelete={() => setDeleteConfirmOpen(true)}
                  onRename={handleRenameFromEditor}
                />
              ) : (
                <EmptyState onOpenDrawer={() => setDrawerOpen(true)} />
              )}
            </Card>
          </div>
        </div>
      </main>

      {/* Dialogs */}
      <CreateTemplateDialog
        open={createTemplateOpen}
        onOpenChange={setCreateTemplateOpen}
        folders={folders}
        defaultFolderId={selectedFolderId}
        onSubmit={handleCreateTemplate}
        isSubmitting={isSaving}
      />
      <CreateFolderDialog
        open={createFolderOpen}
        onOpenChange={setCreateFolderOpen}
        folders={folders}
        defaultParentId={selectedFolderId}
        onSubmit={handleCreateFolder}
        isSubmitting={isFolderApiLoading}
      />
      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        templateName={selectedTemplate?.name ?? ''}
        onConfirm={handleDeleteTemplate}
      />
      <DeleteConfirmDialog
        open={deleteFolderConfirmOpen}
        onOpenChange={setDeleteFolderConfirmOpen}
        templateName={deleteFolderTarget?.name ?? ''}
        onConfirm={handleConfirmDeleteFolder}
      />
      <MoveToDialog
        open={moveDialogOpen}
        onOpenChange={(open) => {
          setMoveDialogOpen(open)
          if (!open) setMoveTarget(null)
        }}
        folders={folders}
        itemName={moveTarget?.name ?? ''}
        itemType={moveTarget?.type ?? 'template'}
        excludeFolderId={moveTarget?.type === 'folder' ? parseInt(moveTarget.id, 10) : null}
        onSubmit={handleMoveDialogSubmit}
        isSubmitting={isSaving || isFolderApiLoading}
      />
      <RenameDialog
        open={renameDialogOpen}
        onOpenChange={(open) => {
          setRenameDialogOpen(open)
          if (!open) setRenameTarget(null)
        }}
        currentName={renameTarget?.name ?? ''}
        itemType={renameTarget?.type ?? 'template'}
        onSubmit={handleRenameSubmit}
        isSubmitting={isSaving || isFolderApiLoading}
      />
    </div>
  )
}
